// policy-handlers.js — Cross-page handlers for Policy / Decisions /
// Agents / Spend.
//
// The Policy page itself (list rendering, rule cards) lives in
// pages/policy.js; the visual rule builder lives in policy-builder.js.
// What's here are the document-level event handlers that wire the
// Policy/Decisions/Agents pages together:
//
//   - Policy: reload, simulate, create/edit/delete/disable, list
//     filter chips, row expand/collapse
//   - Priority: reload (the page is in priority-builder.js)
//   - Decisions: filter chips (source/since), free-text + agent +
//     subsystem filters, override-save in the drawer
//   - Agents: refresh, per-row +Rule, per-row Remove, filter input
//   - Discovered Tools: per-row +Rule → builder deep-link
//   - Spend: window-picker change, repo-chip click
//
// Plus two cross-page "deep-link into the rule builder" helpers used
// by the Agents row buttons and the Discovered Tools row:
//
//   - openPolicyBuilderForAgent(agentID): navigates to Policy, opens
//     the builder, pre-fills principal.agent_id + a sentence stub.
//   - openPolicyBuilderForTool(tool, runtime): opens the builder
//     without navigating, pre-fills action + audit mode + sentence
//     stub.
//
// renderSimulateReport + simulateStatCard render the simulate
// report inline; both are local to handlePolicySimulate.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2p).
import { escapeHTML } from "./util.js";
import { showToast, showConfirm, isCloudMode } from "./ui.js";
import { apiFetch } from "./api.js";
import { navigate } from "./router.js";
import {
  policyState,
  policyListFilter,
  agentsState,
  decisionsState,
  spendState,
} from "./page-state.js";
import {
  openPolicyBuilder,
  openPolicyBuilderForSuggestedRule,
  closePolicyBuilder,
  handleBuilderSave,
  handleRuleEdit,
  handleRuleDelete,
  handleRuleToggleDisabled,
} from "./policy-builder.js";
import { handlePriorityReload } from "./priority-builder.js";
import { fetchAgents, fetchDecisions, removeSession } from "./fetchers.js";
import { renderAgentsPage } from "./pages/agents.js";
import { renderPolicyPage } from "./pages/policy.js";
import { renderDecisionsPage } from "./pages/coordination.js";
import { renderSpendPage, renderFlaggedFeed, showFlagged } from "./pages/spend.js";

// openPolicyBuilderForTool deep-links from the Discovered Tools
// table into the visual rule builder, pre-filling the action and —
// when the runtime prefix maps to a single registered agent — the
// agent_id constraint. Default decision: forbid + audit mode so the
// user defaults to a safe shadow rule rather than accidentally
// hard-blocking something they were just exploring.
//
// Seeds the builder's sentence state (what Save reads), not the legacy
// hidden inputs: filling only those saved `forbid: {}`.
export function openPolicyBuilderForTool(tool, runtime) {
  if (!tool) return;
  openPolicyBuilderForSuggestedRule(
    {
      reason: "Shadow rule for " + runtime + " calling " + tool + ". Flip to Block when ready.",
      decision: "forbid",
      mode: "audit",
      constraint: { action: [tool] },
    },
    "Audit only: logs each " + tool + " call this rule would block.",
  );
}

async function handleAgentRemove(agentID) {
  if (!agentID) return;
  // Replaced native confirm() with the dashboard's standard
  // showConfirm modal so this destructive action looks and behaves
  // like every other Disconnect/Delete prompt in the app. The native
  // dialog leaks the "dashboard.agentcollision.com says" browser
  // chrome and breaks the visual consistency users expect.
  const ok = await showConfirm(
    "Remove session?",
    "Take <code>" +
      escapeHTML(agentID) +
      "</code> off the Sessions list?<br><br>" +
      "<span style=\"color: var(--text-secondary);\">Any files it holds are freed for other chats. Its history stays. If the chat is still running, it comes back the next time it does something.</span>",
    "Remove",
  );
  if (!ok) return;
  const done = removeSession(agentID);
  renderAgentsPage(); // shows "Removing…"
  await done;
  // A failure stays on the row, next to the button, instead of a popup.
  renderAgentsPage();
}

// openPolicyBuilderForAgent opens the visual rule builder modal
// pre-filled with the given agent_id in the principal field, so the
// user can scope a forbid/require_confirm rule to one agent without
// typing the SPIFFE-shaped ID by hand. Default decision is forbid
// since the most common ask is "block agent X."
export function openPolicyBuilderForAgent(agentID) {
  if (!agentID) return;
  navigate("policy");
  setTimeout(() => {
    openPolicyBuilderForSuggestedRule(
      {
        reason: "Scoped rule for agent " + agentID + ".",
        decision: "forbid",
        constraint: { principal: { agent_id: [agentID] } },
      },
      "Pick what this session may not do, then save.",
    );
  }, 50);
}

// showExportedRules shows the rules this page edits as policy.yaml text,
// ready to copy into a repo's .agentcollision/policy.yaml: the machine
// file locally, the team document in cloud mode.
async function showExportedRules() {
  let yaml;
  try {
    if (isCloudMode()) {
      const resp = await apiFetch("/v1/teams/policy");
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || data.error || "HTTP " + resp.status);
      yaml = data.yaml || "";
    } else {
      const resp = await apiFetch("/v1/rules/draft/yaml");
      yaml = await resp.text();
      if (!resp.ok) throw new Error(yaml || "HTTP " + resp.status);
    }
  } catch (err) {
    showToast("error", "Could not load the rules: " + (err.message || err));
    return;
  }
  if (!yaml.trim()) {
    showToast("info", "No rules saved here yet.");
    return;
  }
  const copy = await showConfirm(
    "Exported rules",
    '<pre style="max-height: 50vh; overflow: auto; text-align: left; font-size: 12px; padding: 10px; background: var(--bg-soft); border-radius: var(--r-sm);">' +
      escapeHTML(yaml) +
      "</pre>",
    "Copy",
  );
  if (!copy) return;
  try {
    await navigator.clipboard.writeText(yaml);
    showToast("success", "Copied. Paste it into .agentcollision/policy.yaml and commit.");
  } catch (err) {
    showToast("error", "Copy failed: " + (err.message || err));
  }
}

// handlePolicyReload POSTs to /v1/rules/reload and refreshes the
// Policy tab. Equivalent to running `agc policy reload` or sending
// SIGHUP to the daemon — all three call the same reload closure on
// the daemon side.
async function handlePolicyReload(btn) {
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Reloading…";
  try {
    const resp = await apiFetch("/v1/rules/reload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || "HTTP " + resp.status);
    }
    // Decode the diff summary so the toast can tell the user whether
    // their edit actually landed or was a no-op against an already-
    // loaded ruleset (a common cause of confusion when users edit
    // policy.yaml and don't see new behavior).
    let summary = "Policy reloaded from disk.";
    try {
      const data = await resp.json();
      const changes = (data && data.changes) || [];
      if (changes.length === 0) {
        summary = "Policy reloaded — no changes (file was identical).";
      } else {
        summary =
          "Policy reloaded — " +
          changes.length +
          " change" +
          (changes.length === 1 ? "" : "s") +
          ".";
      }
    } catch (_) {
      // Older daemon won't have changes — fall through to default.
    }
    // Force the Policy tab to re-fetch + re-render.
    policyState.data = null;
    await renderPolicyPage();
    btn.disabled = false;
    btn.textContent = orig;
    showToast("success", summary);
  } catch (err) {
    btn.disabled = false;
    btn.textContent = orig;
    showToast("error", "Reload failed: " + (err.message || err));
  }
}

// handleDecisionOverrideSave POSTs the user-authored body to
// /v1/decisions/override (same code path as `agc decision`).
// Resolves the lease ID from the button's data attribute and the
// body from the textarea above it. On success: refresh the decisions
// cache + re-render so the row flips from auto-stub → user-edited
// without a manual page reload.
async function handleDecisionOverrideSave(btn) {
  const leaseID = btn.dataset.decisionLeaseId || "";
  const txt = document.getElementById("decision-override-text");
  const body = ((txt && txt.value) || "").trim();
  if (!leaseID) {
    showToast("error", "Override missing lease ID — close and re-open the drawer.");
    return;
  }
  if (!body) {
    showToast("error", "Override body cannot be empty.");
    return;
  }
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Saving…";
  try {
    const resp = await apiFetch("/v1/decisions/override", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lease_id: leaseID, body: body }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || "HTTP " + resp.status);
    }
    showToast("success", "Decision override saved.");
    // Close the drawer and force a re-fetch so the list shows the
    // user-edited badge instead of auto-stub.
    const backdrop = document.getElementById("drawer-backdrop");
    if (backdrop) backdrop.classList.remove("open");
    decisionsState.list = [];
    await fetchDecisions();
    renderDecisionsPage();
  } catch (err) {
    btn.disabled = false;
    btn.textContent = orig;
    showToast("error", "Override failed: " + (err.message || err));
  }
}

// handlePolicySimulate POSTs to /v1/rules/simulate and renders the
// resulting report into the simulate panel above the policy body.
// Default 7d window, no event cap. The dashboard surface for `ac
// policy simulate` — same engine, same data, just a browser-friendly
// wrapper so platform-engineer buyers can run a what-if without
// dropping into the CLI.
async function handlePolicySimulate(btn) {
  const panel = document.getElementById("policy-simulate-panel");
  const body = document.getElementById("policy-simulate-body");
  if (!panel || !body) return;
  panel.style.display = "";
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Simulating…";
  body.innerHTML =
    '<div class="empty"><div class="empty-title">Replaying audit events through the loaded ruleset…</div></div>';
  try {
    const resp = await apiFetch("/v1/rules/simulate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ since_hours: 168, limit: 0 }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || "HTTP " + resp.status);
    }
    const data = await resp.json();
    renderSimulateReport(body, data);
  } catch (err) {
    body.innerHTML =
      '<div class="empty"><div class="empty-title">Simulation failed</div><div class="empty-detail">' +
      escapeHTML(err.message || String(err)) +
      "</div></div>";
  } finally {
    btn.disabled = false;
    btn.textContent = orig;
  }
}

// renderSimulateReport paints the SimulateReport into the panel
// body. Two summary stats up top (would-block / would-require-
// confirm), then a per-rule table with hit counts + sample
// event_ids, then a per-actor table for the top-affected agents.
function renderSimulateReport(body, data) {
  const rep = (data && data.report) || {};
  const ruleCount = data && data.rule_count;
  const win = (data && data.window_hours) || 168;

  let html = "";
  html += '<div style="padding: 16px 20px;">';
  html += '<div style="font-size: 13px; color: var(--text-secondary); margin-bottom: 12px;">';
  html += "Replayed <strong>" + (rep.events_examined || 0) + "</strong> audit events from the last ";
  html +=
    "<strong>" +
    win +
    "h</strong> through " +
    (ruleCount || 0) +
    " loaded rule" +
    (ruleCount === 1 ? "" : "s") +
    ". ";
  html +=
    "Of those, <strong>" +
    (rep.events_simulable || 0) +
    "</strong> were policy-relevant (lease.acquired etc.).";
  html += "</div>";

  // Big-number summary cards.
  html += '<div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 16px;">';
  html += simulateStatCard("Would block", rep.would_block || 0, "var(--danger)");
  html += simulateStatCard(
    "Would require confirm",
    rep.would_require_confirm || 0,
    "var(--warning, #f59e0b)",
  );
  html += simulateStatCard("Would allow", rep.would_allow || 0, "var(--success, #22c55e)");
  html += "</div>";

  // Per-rule hits.
  const perRule = rep.per_rule || [];
  if (perRule.length > 0) {
    html += '<div style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.05em; margin-bottom: 6px;">Rule firings</div>';
    html += '<table class="lease-table" style="width: 100%; margin-bottom: 16px;"><thead><tr><th>Rule</th><th>Decision</th><th style="text-align: right;">Count</th><th>Sample event_ids</th></tr></thead><tbody>';
    for (const h of perRule) {
      const sample = (h.sample || []).slice(0, 3).join(", ");
      html += "<tr><td><strong>" + escapeHTML(h.rule_name || "(unnamed)") + "</strong></td>";
      html += "<td><code>" + escapeHTML(h.decision || "") + "</code></td>";
      html += '<td class="age-cell">' + h.count + "</td>";
      html +=
        '<td style="font-size: 11px; color: var(--text-muted);">' +
        escapeHTML(sample) +
        "</td></tr>";
    }
    html += "</tbody></table>";
  }

  // Top affected actors.
  const perActor = rep.per_actor || [];
  if (perActor.length > 0) {
    html += '<div style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.05em; margin-bottom: 6px;">Top affected agents</div>';
    html += '<table class="lease-table" style="width: 100%; margin-bottom: 16px;"><thead><tr><th>Agent</th><th style="text-align: right;">Block count</th></tr></thead><tbody>';
    for (const a of perActor.slice(0, 10)) {
      html +=
        "<tr><td><code>" +
        escapeHTML(a.agent_id || "") +
        '</code></td><td class="age-cell">' +
        a.count +
        "</td></tr>";
    }
    html += "</tbody></table>";
  }

  // Limitations the simulator surfaced.
  const lims = rep.limitations || [];
  if (lims.length > 0) {
    html += '<div class="help-block" style="margin-top: 12px; font-size: 12px;">';
    html += '<strong>Caveats:</strong><ul style="margin: 6px 0 0; padding-left: 20px;">';
    for (const l of lims) html += "<li>" + escapeHTML(l) + "</li>";
    html += "</ul></div>";
  }

  if ((rep.events_simulable || 0) === 0) {
    html +=
      '<div class="empty" style="padding: 24px;"><div class="empty-title">No policy-relevant events in this window</div><div class="empty-detail">Either no agents were active in the last ' +
      win +
      "h, or every event was a non-simulable type (lease.released, lease.expired, etc.). Try a wider window.</div></div>";
  }

  html += "</div>";
  body.innerHTML = html;
}

function simulateStatCard(label, count, color) {
  return (
    '<div style="padding: 14px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-soft);">' +
    '<div style="font-size: 11px; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.05em; margin-bottom: 4px;">' +
    label +
    "</div>" +
    '<div style="font-size: 28px; font-weight: 700; color: ' +
    color +
    ';">' +
    count +
    "</div>" +
    "</div>"
  );
}

// ---- module init: delegated handlers ----

// ===== Templates gallery =====
//
// State lives on policyState.templates so we cache the list across
// modal opens (saves a round-trip and lets us update the rendered
// "this rule already exists" hint per-card after an install or local
// rule add/delete).

async function openTemplatesGallery() {
  const modal = document.getElementById("policy-templates-modal");
  if (!modal) return;
  modal.style.display = "flex";
  await loadAndRenderTemplates();
}

function closeTemplatesGallery() {
  const modal = document.getElementById("policy-templates-modal");
  if (modal) modal.style.display = "none";
}

async function loadAndRenderTemplates() {
  const body = document.getElementById("policy-templates-body");
  if (!body) return;
  try {
    const list = await apiFetch("/v1/rules/templates");
    const data = await list.json();
    const templates = (data && data.templates) || [];
    if (templates.length === 0) {
      body.innerHTML =
        '<div class="empty"><div class="empty-title">No templates registered</div></div>';
      return;
    }
    // Render skeletons immediately; fetch full bodies in parallel.
    body.innerHTML = templates.map(renderTemplateCardSkeleton).join("");
    // Hydrate each card's preview (parsed rules count + YAML body) in
    // parallel so the gallery feels instant even on slow links.
    await Promise.all(
      templates.map(async (t) => {
        try {
          const r = await apiFetch("/v1/rules/templates/" + encodeURIComponent(t.name));
          const full = await r.json();
          const preview = document.getElementById("tmpl-preview-" + t.name);
          if (preview) {
            preview.innerHTML = renderTemplateYAMLPreview(full.yaml || "");
          }
          const ruleCount = countRulesInYAML(full.yaml || "");
          const meta = document.getElementById("tmpl-meta-" + t.name);
          if (meta) {
            meta.textContent = ruleCount + " rule" + (ruleCount === 1 ? "" : "s");
          }
        } catch (_) {
          // Best-effort: card stays in its skeleton state if hydrate
          // fails. User can close + retry.
        }
      }),
    );
  } catch (err) {
    body.innerHTML =
      '<div class="empty"><div class="empty-title">Could not load templates</div><div class="empty-detail">' +
      escapeHTML(err.message || "unknown") +
      "</div></div>";
  }
}

function renderTemplateCardSkeleton(t) {
  const safeName = escapeHTML(t.name);
  const safeDesc = escapeHTML(t.description || "");
  return (
    '<div class="card" style="margin-bottom: 12px; padding: 14px 16px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-panel-solid);">' +
    '<div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; cursor: pointer;" data-template-toggle="' +
    safeName +
    '">' +
    '<div style="flex: 1;">' +
    '<div style="font-weight: 600; color: var(--text-display); font-size: 14px;">' +
    safeName +
    "</div>" +
    '<div style="margin-top: 4px; font-size: 13px; color: var(--text-secondary); line-height: 1.5;">' +
    safeDesc +
    "</div>" +
    '<div id="tmpl-meta-' +
    safeName +
    '" style="margin-top: 6px; font-size: 11px; color: var(--text-tertiary);">loading…</div>' +
    "</div>" +
    '<button class="btn btn-sm btn-primary" data-install-template="' +
    safeName +
    '" style="flex-shrink: 0;">Install</button>' +
    "</div>" +
    '<div id="tmpl-preview-' +
    safeName +
    '" style="display: none; margin-top: 12px; padding: 12px; background: var(--bg-soft); border-radius: var(--r-sm); font-family: var(--font-mono); font-size: 11px; line-height: 1.5; max-height: 320px; overflow: auto;"></div>' +
    "</div>"
  );
}

function renderTemplateYAMLPreview(yaml) {
  // Plain pre — the YAML is short enough that syntax highlighting
  // would be cost without payoff. Escape for safety.
  return "<pre style=\"margin: 0; white-space: pre-wrap;\">" + escapeHTML(yaml) + "</pre>";
}

function countRulesInYAML(yaml) {
  // Cheap rule count by matching top-level "- name:" lines. Mirrors
  // the parser's shape but avoids importing yaml just for a count.
  const m = yaml.match(/^\s*-\s+name:/gm);
  return m ? m.length : 0;
}

async function handleTemplateInstall(name, btn) {
  if (!name) return;
  const ok = await showConfirm(
    "Install '" + name + "' template?",
    "This will add the template's rules to your policy. Existing rules stay intact. If any rule name in the template collides with one you already have, the install will fail and tell you which ones to rename first.",
    "Install",
  );
  if (!ok) return;
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Installing…";
  }
  try {
    const r = await apiFetch(
      "/v1/rules/templates/" + encodeURIComponent(name) + "/install",
      { method: "POST" },
    );
    if (r.ok) {
      const data = await r.json();
      showToast(
        "success",
        "Installed '" + name + "' (" + (data.rules_added || 0) + " rules added)",
      );
      closeTemplatesGallery();
      // Force-refresh the policy data so the new rules show up.
      policyState.data = null;
      await renderPolicyPage();
      return;
    }
    const err = await r.json().catch(() => ({}));
    if (r.status === 409 && err.conflicts) {
      showToast(
        "error",
        "Name conflict: " +
          err.conflicts.join(", ") +
          ". Rename or delete those rules, then retry.",
      );
    } else {
      showToast("error", err.message || "Install failed (HTTP " + r.status + ")");
    }
  } catch (e) {
    showToast("error", "Install failed: " + (e.message || "network error"));
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = "Install";
    }
  }
}

function wireListeners() {
  // ----- Spend page -----

  // Window-picker change handler — re-fetch + re-render. Invalidates the
  // cached per-chat slot since it's keyed to the window.
  document.addEventListener("change", (e) => {
    if (e.target.id === "spend-window") {
      spendState.window = e.target.value;
      spendState.dataByChat = null;
      // New window = different row set; jump back to page 1 so the
      // offset isn't past the end of a narrower window.
      spendState.page = 0;
      renderSpendPage();
    }
  });

  // Repo chip click handler — set filter, re-render only (no
  // re-fetch needed since chip filter is client-side).
  document.addEventListener("click", (e) => {
    const chip = e.target.closest(".spend-repo-chip");
    if (!chip) return;
    spendState.repoFilter = chip.getAttribute("data-repo") || "";
    // Changing the repo lens changes the visible set; reset to page 1.
    spendState.page = 0;
    renderSpendPage();
  });

  // Collision row expand/collapse — click a Recent row to toggle its full
  // writeup. Guarded so clicking the copy-id chip or the "view in Events"
  // deep-link inside the row does their own thing instead of toggling.
  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-copy]") || e.target.closest("[data-page-link]"))
      return;
    const row = e.target.closest(".spend-ix-clickable");
    if (!row) return;
    const id = row.getAttribute("data-ix-id");
    if (!id) return;
    if (!spendState.expandedIncidents) spendState.expandedIncidents = new Set();
    if (spendState.expandedIncidents.has(id))
      spendState.expandedIncidents.delete(id);
    else spendState.expandedIncidents.add(id);
    renderSpendPage();
  });

  // "Show all / Show fewer" toggle in the Recent band footer — flips the
  // inline cap so every incident the payload carries renders in-place, no trip
  // to the Events page needed. Sits outside .spend-ix-clickable so it never
  // collides with the row-expand handler above.
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-ix-showall]");
    if (!btn) return;
    spendState.showAllIncidents =
      btn.getAttribute("data-ix-showall") === "expand";
    renderSpendPage();
  });

  // The search runs on the server; wait for a pause in typing.
  let flaggedTimer = 0;
  document.addEventListener("input", (e) => {
    if (e.target.id !== "flagged-filter") return;
    clearTimeout(flaggedTimer);
    const value = e.target.value || "";
    flaggedTimer = setTimeout(() => {
      spendState.flaggedFilter = value;
      spendState.flaggedPage = 0;
      renderFlaggedFeed();
    }, 250);
  });

  // A Protection number that narrows the flagged list (hosted conflicts and
  // handoffs), and the list's "Show all" button.
  const flaggedLink = (e) => {
    const only = e.target.closest("[data-flagged-types]");
    if (only) {
      e.preventDefault();
      showFlagged({ types: only.dataset.flaggedTypes.split(","), label: only.dataset.flaggedLabel });
      return;
    }
    if (e.target.closest("[data-flagged-clear]")) showFlagged(null);
  };
  document.addEventListener("click", flaggedLink);
  document.addEventListener("keydown", (e) => {
    if ((e.key === "Enter" || e.key === " ") && e.target.closest("[data-flagged-types]")) flaggedLink(e);
  });

  // Flagged-event feed pager (Prev / Next under the feed).
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-flagged-page]");
    if (!btn || btn.disabled) return;
    if (btn.dataset.flaggedPage === "prev") {
      spendState.flaggedPage = Math.max(0, (spendState.flaggedPage || 0) - 1);
    } else {
      spendState.flaggedPage = (spendState.flaggedPage || 0) + 1;
    }
    renderFlaggedFeed();
  });

  // Spend search input — sends the query to the server (?q=) so it matches
  // across ALL rows, not just the visible page. Debounced (250ms) so each
  // keystroke doesn't fire a request; resets to page 1 since the row set
  // changes. Unlike the other filter inputs (in-memory), this re-fetches.
  let spendSearchTimer = null;
  document.addEventListener("input", (e) => {
    if (e.target.id !== "spend-filter") return;
    const val = e.target.value;
    if (spendSearchTimer) clearTimeout(spendSearchTimer);
    spendSearchTimer = setTimeout(() => {
      spendState.filterText = val;
      spendState.page = 0;
      spendState.dataByChat = null;
      renderSpendPage();
    }, 250);
  });

  // ----- Policy / Decisions / Agents click handler -----
  //
  // Delegated click handler for the Policy page header buttons
  // (Reload + Simulate), visual rule builder controls, Priority
  // reload, Decisions filter chips + drawer override, Agents page
  // refresh + per-row buttons, Discovered Tools "+Rule" deep-link,
  // and Policy rule card Edit/Delete/Disable/expand/collapse.
  document.addEventListener("click", (e) => {
    if (e.target.id === "policy-reload-btn") {
      handlePolicyReload(e.target);
      return;
    }
    if (e.target.id === "policy-simulate-btn") {
      handlePolicySimulate(e.target);
      return;
    }
    if (e.target.id === "policy-simulate-close") {
      const panel = document.getElementById("policy-simulate-panel");
      if (panel) panel.style.display = "none";
      return;
    }
    // Visual rule builder buttons. The modal lives in the static
    // page DOM, so wiring once via delegation is enough; the helpers
    // below all already exist (openPolicyBuilder etc.).
    if (e.target.id === "policy-create-btn") {
      openPolicyBuilder();
      return;
    }
    if (e.target.id === "policy-show-yaml-link") {
      e.preventDefault();
      showExportedRules();
      return;
    }
    if (e.target.id === "policy-templates-btn") {
      openTemplatesGallery();
      return;
    }
    if (e.target.id === "policy-templates-close" || e.target.id === "policy-templates-close-2") {
      closeTemplatesGallery();
      return;
    }
    // Click on a per-template Install button. The rendered card stamps
    // data-install-template="<name>" on each Install action; this
    // single delegated handler fans out to handleTemplateInstall.
    const installBtn = e.target.closest("[data-install-template]");
    if (installBtn) {
      handleTemplateInstall(installBtn.getAttribute("data-install-template"), installBtn);
      return;
    }
    // Click on a template card header to expand/collapse its preview.
    const tmplCard = e.target.closest("[data-template-toggle]");
    if (tmplCard && !e.target.closest("[data-install-template]")) {
      const name = tmplCard.getAttribute("data-template-toggle");
      const preview = document.getElementById("tmpl-preview-" + name);
      if (preview) {
        preview.style.display = preview.style.display === "none" ? "block" : "none";
      }
      return;
    }
    if (
      e.target.id === "policy-builder-close" ||
      e.target.id === "policy-builder-close-2"
    ) {
      closePolicyBuilder();
      return;
    }
    if (e.target.id === "policy-builder-save") {
      handleBuilderSave(e.target);
      return;
    }
    // Priority page Reload button. The handler exists at
    // handlePriorityReload but was never bound. Same pattern as
    // policy-reload-btn above.
    if (e.target.id === "priority-reload-btn") {
      handlePriorityReload(e.target);
      return;
    }
    // Decisions page filter chips. Source chips toggle between all/
    // user-edited/auto-stub; Since chips toggle the time window.
    // Both update decisionsState and re-render.
    const sourceChip = e.target.closest("[data-decision-source]");
    if (sourceChip) {
      decisionsState.source = sourceChip.dataset.decisionSource || "all";
      document.querySelectorAll("[data-decision-source]").forEach((c) => {
        c.classList.toggle("active", c === sourceChip);
      });
      renderDecisionsPage();
      return;
    }
    const sinceChip = e.target.closest("[data-decision-since]");
    if (sinceChip) {
      decisionsState.sinceSeconds =
        parseInt(sinceChip.dataset.decisionSince || "0", 10) || 0;
      document.querySelectorAll("[data-decision-since]").forEach((c) => {
        c.classList.toggle("active", c === sinceChip);
      });
      renderDecisionsPage();
      return;
    }
    if (e.target.id === "decisions-clear-filters") {
      decisionsState.filter = "";
      decisionsState.source = "all";
      decisionsState.subsystem = "";
      decisionsState.sinceSeconds = 0;
      decisionsState.agent = "";
      // Reset every input + chip to its default visual state.
      const fInput = document.getElementById("decisions-filter");
      if (fInput) fInput.value = "";
      const aInput = document.getElementById("decisions-agent-filter");
      if (aInput) aInput.value = "";
      const sub = document.getElementById("decisions-subsystem-filter");
      if (sub) sub.value = "";
      document.querySelectorAll("[data-decision-source]").forEach((c) => {
        c.classList.toggle("active", c.dataset.decisionSource === "all");
      });
      document.querySelectorAll("[data-decision-since]").forEach((c) => {
        c.classList.toggle("active", c.dataset.decisionSince === "0");
      });
      renderDecisionsPage();
      return;
    }
    // Decision drawer override-save. The button is injected into the
    // drawer body so we resolve the lease ID from the data attribute,
    // not from any module-level state.
    if (e.target.id === "decision-override-save") {
      handleDecisionOverrideSave(e.target);
      return;
    }
    // Agents page: refresh, per-row +Rule, per-row Remove.
    if (e.target.id === "agents-refresh-btn") {
      agentsState.list = [];
      fetchAgents().then(renderAgentsPage);
      return;
    }
    const scopeBtn = e.target.closest(".agents-scope-btn");
    if (scopeBtn) {
      openPolicyBuilderForAgent(scopeBtn.dataset.agentId || "");
      return;
    }
    const removeBtn = e.target.closest(".agents-remove-btn");
    if (removeBtn) {
      handleAgentRemove(removeBtn.dataset.agentId || "");
      return;
    }
    // Discovered Tools row: "+ Rule" deep-links into the builder
    // pre-filled with the action + runtime + audit mode.
    const dtBtn = e.target.closest(".discovered-tool-rule-btn");
    if (dtBtn) {
      openPolicyBuilderForTool(
        dtBtn.dataset.tool || "",
        dtBtn.dataset.runtime || "",
      );
      return;
    }
    // Policy rule card: Disable / Edit / Delete buttons.
    // Disable / Enable toggle. Both directions go through a
    // confirmation modal — flipping enforcement on/off is a policy-
    // impact decision and we don't want it to be a one-click
    // accident. The button label flips based on current state
    // (data-currently-disabled="0" → button reads "Disable").
    const ruleDisableBtn = e.target.closest(".policy-rule-disable-btn");
    if (ruleDisableBtn) {
      handleRuleToggleDisabled(
        ruleDisableBtn.dataset.ruleName || "",
        ruleDisableBtn.dataset.currentlyDisabled === "1",
        ruleDisableBtn,
      );
      return;
    }
    const ruleEditBtn = e.target.closest(".policy-rule-edit-btn");
    if (ruleEditBtn) {
      handleRuleEdit(ruleEditBtn.dataset.ruleName || "");
      return;
    }
    const ruleDeleteBtn = e.target.closest(".policy-rule-delete-btn");
    if (ruleDeleteBtn) {
      handleRuleDelete(ruleDeleteBtn.dataset.ruleName || "", ruleDeleteBtn);
      return;
    }
    // Policy list filter chips + clear + row toggle.
    const filterChip = e.target.closest("[data-policy-filter-decision]");
    if (filterChip) {
      policyListFilter.decision = filterChip.dataset.policyFilterDecision;
      renderPolicyPage();
      return;
    }
    if (e.target && e.target.id === "policy-filter-clear") {
      policyListFilter.text = "";
      policyListFilter.decision = "all";
      renderPolicyPage();
      return;
    }
    // Row expand/collapse — clicking the toggle or the sentence area.
    const ruleToggle = e.target.closest(".policy-rule-toggle");
    if (ruleToggle) {
      const name = ruleToggle.dataset.ruleName || "";
      if (policyListFilter.expanded.has(name))
        policyListFilter.expanded.delete(name);
      else policyListFilter.expanded.add(name);
      renderPolicyPage();
      return;
    }
    const ruleSentence = e.target.closest(".policy-rule-sentence");
    if (ruleSentence) {
      const name = ruleSentence.dataset.ruleName || "";
      if (policyListFilter.expanded.has(name))
        policyListFilter.expanded.delete(name);
      else policyListFilter.expanded.add(name);
      renderPolicyPage();
      return;
    }
  });

  // Filter input — live as you type.
  document.addEventListener("input", (e) => {
    if (e.target && e.target.id === "policy-list-filter") {
      policyListFilter.text = e.target.value || "";
      renderPolicyPage();
    }
  });

  // Decisions page text + select inputs. `input` (not `change`)
  // gives a live filter as the user types; the filter logic is
  // pure-JS over the already-fetched list, so latency is fine.
  document.addEventListener("input", (e) => {
    if (e.target.id === "decisions-filter") {
      decisionsState.filter = e.target.value || "";
      renderDecisionsPage();
      return;
    }
    if (e.target.id === "decisions-agent-filter") {
      decisionsState.agent = e.target.value || "";
      renderDecisionsPage();
      return;
    }
    if (e.target.id === "agents-filter") {
      agentsState.filter = e.target.value || "";
      renderAgentsPage();
      return;
    }
  });
  document.addEventListener("change", (e) => {
    if (e.target.id === "decisions-subsystem-filter") {
      decisionsState.subsystem = e.target.value || "";
      renderDecisionsPage();
      return;
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
